/**
 * Lógica de las series temporales. Pura, sin React y sin SVG: es lo que hay
 * que poder probar sin montar nada.
 *
 * El problema que resuelve el bucketing
 * -------------------------------------
 * `GET /analytics/timeseries` devuelve **una fila por día y por resultado**.
 * Un rango de doce meses con siete resultados son hasta 2.555 filas, y
 * dibujarlas como 2.555 segmentos sobre un gráfico de 800 px de ancho no sólo
 * es más caro: es peor de leer, porque hay tres puntos por píxel y el trazo se
 * convierte en una banda de ruido donde no se distingue ninguna tendencia.
 *
 * Así que se agrega en dos pasos —por día primero, en tramos de N días
 * después— y el gráfico nunca dibuja más de `MAX_CHART_POINTS` puntos, sea el
 * rango de una semana o de trece meses. Ese es el criterio de aceptación
 * "los gráficos manejan rangos de 12 meses sin degradarse" del issue #44.
 *
 * Los días sin ningún escaneo no vienen en la respuesta y acá se rellenan con
 * cero. Un día sin escaneos es un cero, no un hueco: sin rellenar, la línea
 * une el viernes con el lunes y muestra una meseta donde hubo un fin de semana
 * parado.
 */
import type { Schemas } from '@/api/types';

export type TimeseriesResponse = Schemas['TimeseriesOut'];
export type SeriesPoint = Schemas['SeriesPoint'];
export type OverviewResponse = Schemas['OverviewOut'];

/** `services/api/analytics/queries.py::SUSPICIOUS_RESULTS`. */
export const SUSPICIOUS_RESULTS: ReadonlySet<number> = new Set([2, 3, 4, 5, 6]);
export const RESULT_GENUINE = 0;
export const RESULT_ALREADY_SEEN = 1;

/** Tope de puntos dibujados. Ver el encabezado. */
export const MAX_CHART_POINTS = 90;

export interface DayTotal {
  /** `AAAA-MM-DD`, tal como lo devuelve la API. */
  day: string;
  scans: number;
  genuine: number;
  alreadySeen: number;
  suspicious: number;
  units: number;
}

function emptyDay(day: string): DayTotal {
  return { day, scans: 0, genuine: 0, alreadySeen: 0, suspicious: 0, units: 0 };
}

/** Suma los resultados de cada día en una sola fila por día. */
export function dailyTotals(points: readonly SeriesPoint[]): DayTotal[] {
  const byDay = new Map<string, DayTotal>();
  for (const point of points) {
    const day = point.day.slice(0, 10);
    let total = byDay.get(day);
    if (total === undefined) {
      total = emptyDay(day);
      byDay.set(day, total);
    }
    total.scans += point.scans;
    total.units += point.units;
    if (point.result === RESULT_GENUINE) total.genuine += point.scans;
    else if (point.result === RESULT_ALREADY_SEEN) total.alreadySeen += point.scans;
    if (SUSPICIOUS_RESULTS.has(point.result)) total.suspicious += point.scans;
  }
  return [...byDay.values()].sort((a, b) => a.day.localeCompare(b.day));
}

const DAY_MS = 86_400_000;

function isoDay(time: number): string {
  return new Date(time).toISOString().slice(0, 10);
}

/**
 * Rellena con ceros los días que la API no devolvió.
 *
 * Se calcula en UTC y no con el huso del tenant a propósito: `from_day` y
 * `to_day` ya vienen resueltos por el servidor y son fechas, no instantes.
 * Reinterpretarlas en otro huso las correría un día.
 */
export function fillDays(totals: readonly DayTotal[], from: string, to: string): DayTotal[] {
  const start = Date.parse(`${from.slice(0, 10)}T00:00:00Z`);
  const end = Date.parse(`${to.slice(0, 10)}T00:00:00Z`);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return [...totals];

  const known = new Map(totals.map((total) => [total.day, total]));
  const filled: DayTotal[] = [];
  for (let time = start; time <= end; time += DAY_MS) {
    const day = isoDay(time);
    filled.push(known.get(day) ?? emptyDay(day));
  }
  return filled;
}

export interface Bucket extends DayTotal {
  /** Primer día del tramo. `day` repite este valor para las etiquetas. */
  from: string;
  /** Último día del tramo. Igual a `from` cuando el tramo es de un día. */
  to: string;
  /** Cuántos días reales se sumaron. */
  days: number;
}

/**
 * Agrupa días consecutivos en tramos de igual tamaño hasta que quedan
 * `maxPoints` o menos.
 *
 * Los tramos son de tamaño fijo y no "por semana" o "por mes": semanas y meses
 * tienen largos distintos, y un gráfico donde una barra vale 28 días y la de al
 * lado 31 exagera febrero en un 10 % sin avisar.
 */
export function bucketSeries(days: readonly DayTotal[], maxPoints = MAX_CHART_POINTS): Bucket[] {
  if (days.length === 0) return [];
  const size = Math.max(1, Math.ceil(days.length / Math.max(1, maxPoints)));
  const buckets: Bucket[] = [];

  for (let start = 0; start < days.length; start += size) {
    const slice = days.slice(start, start + size);
    const first = slice[0];
    const last = slice[slice.length - 1];
    if (first === undefined || last === undefined) continue;
    const bucket: Bucket = {
      day: first.day,
      from: first.day,
      to: last.day,
      days: slice.length,
      scans: 0,
      genuine: 0,
      alreadySeen: 0,
      suspicious: 0,
      units: 0,
    };
    for (const entry of slice) {
      bucket.scans += entry.scans;
      bucket.genuine += entry.genuine;
      bucket.alreadySeen += entry.alreadySeen;
      bucket.suspicious += entry.suspicious;
      bucket.units += entry.units;
    }
    buckets.push(bucket);
  }
  return buckets;
}

/** Atajo: respuesta cruda → puntos listos para el gráfico. */
export function chartSeries(
  response: TimeseriesResponse,
  maxPoints = MAX_CHART_POINTS,
): Bucket[] {
  return bucketSeries(fillDays(dailyTotals(response.points), response.from_day, response.to_day), maxPoints);
}

// ───────────────────────────────────────────────────────────────── geometría

/**
 * Tope "redondo" del eje: 1, 2 o 5 por la potencia de diez que corresponda.
 * Un eje que termina en 4.837 obliga a leer el número para saber la escala.
 */
export function niceMax(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalized = value / magnitude;
  const step = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return step * magnitude;
}

export interface ChartBox {
  width: number;
  height: number;
}

/** Coordenadas de la serie dentro de la caja, en píxeles del viewBox. */
export function seriesPoints(
  values: readonly number[],
  max: number,
  box: ChartBox,
): { x: number; y: number }[] {
  if (values.length === 0) return [];
  const top = max <= 0 ? 1 : max;
  const step = values.length === 1 ? 0 : box.width / (values.length - 1);
  return values.map((value, index) => ({
    x: values.length === 1 ? box.width / 2 : index * step,
    y: box.height - (Math.max(0, value) / top) * box.height,
  }));
}

function round(value: number): string {
  return (Math.round(value * 100) / 100).toString();
}

export function linePath(points: readonly { x: number; y: number }[]): string {
  if (points.length === 0) return '';
  return points
    .map((point, index) => `${index === 0 ? 'M' : 'L'}${round(point.x)} ${round(point.y)}`)
    .join(' ');
}

/** El mismo trazo cerrado contra la base, para el relleno. */
export function areaPath(points: readonly { x: number; y: number }[], box: ChartBox): string {
  if (points.length === 0) return '';
  const first = points[0];
  const last = points[points.length - 1];
  if (first === undefined || last === undefined) return '';
  return `${linePath(points)} L${round(last.x)} ${round(box.height)} L${round(first.x)} ${round(box.height)} Z`;
}
